import { useEffect, useState } from 'react';
import { Input } from '@heroui/react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { MagnifyingGlassIcon } from '@/icons';

/**
 * Debounced search box. Emits `onSearch` only after the user pauses typing, so
 * list views don't refetch on every keystroke. `value` seeds it from URL state.
 */
export function SearchInput({ value = '', onSearch, placeholder = 'Search…', delay = 300, className, ariaLabel = 'Search' }: {
  value?: string;
  onSearch: (q: string) => void;
  placeholder?: string;
  delay?: number;
  className?: string;
  ariaLabel?: string;
}) {
  const [term, setTerm] = useState(value);
  const debounced = useDebouncedValue(term, delay);

  useEffect(() => { if (debounced !== value) onSearch(debounced); }, [debounced]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Input
      value={term}
      onValueChange={setTerm}
      placeholder={placeholder}
      startContent={<MagnifyingGlassIcon className="h-4 w-4 text-fg-subtle" />}
      variant="bordered"
      size="sm"
      className={className}
      aria-label={ariaLabel}
      isClearable
      onClear={() => { setTerm(''); onSearch(''); }}
    />
  );
}
